import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ForgotPassword = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setSent(true);
      toast({
        title: language === 'es' ? '¡Correo enviado!' : 'Email Sent!',
        description: language === 'es' 
          ? 'Revisa tu bandeja de entrada para el enlace de restablecimiento.' 
          : 'Check your inbox for the reset link.',
      });
    } catch (error) {
      toast({
        title: language === 'es' ? 'Error' : 'Error',
        description: error.response?.data?.detail || 
          (language === 'es' ? 'No se pudo enviar el correo' : 'Failed to send email'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center py-32 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_75f4a617-7223-46ab-b5af-9fb0ce34d58c/artifacts/s7ixbz5d_customcolor_logo_transparent_background.png" 
            alt="Exciting.Travel" 
            className="h-20 w-auto mx-auto mb-6"
          />
          <h1 className="text-4xl font-black text-white mb-2">
            {language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot Password?'}
          </h1>
          <p className="text-gray-400">
            {language === 'es' 
              ? 'No te preocupes, te enviaremos instrucciones.' 
              : "No worries, we'll send you reset instructions."}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="font-bold mb-2 flex items-center space-x-2">
                  <Mail size={16} className="text-[#84bc00]" />
                  <span>{language === 'es' ? 'Correo electrónico' : 'Email'}</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-2"
                  placeholder={language === 'es' ? 'tu@correo.com' : 'your@email.com'}
                  data-testid="forgot-password-email"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold py-6 text-lg"
                data-testid="forgot-password-submit"
              >
                {loading 
                  ? (language === 'es' ? 'Enviando...' : 'Sending...') 
                  : (language === 'es' ? 'Enviar enlace de restablecimiento' : 'Send Reset Link')}
              </Button>
            </form>
          ) : (
            <div className="text-center">
              <div className="bg-[#84bc00]/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#84bc00]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {language === 'es' ? '¡Revisa tu correo!' : 'Check Your Email!'}
              </h2>
              <p className="text-gray-600 mb-6">
                {language === 'es' 
                  ? `Hemos enviado instrucciones para restablecer tu contraseña a ${email}` 
                  : `We've sent password reset instructions to ${email}`}
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {language === 'es' ? '¿No recibiste el correo?' : "Didn't receive it?"}
                </h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• {language === 'es' ? 'Revisa tu carpeta de spam' : 'Check your spam folder'}</li>
                  <li>• {language === 'es' ? 'El correo puede tardar unos minutos' : 'The email may take a few minutes'}</li>
                </ul>
              </div>
              <Button
                onClick={() => setSent(false)}
                variant="outline"
                className="mt-6"
              >
                {language === 'es' ? 'Intentar con otro correo' : 'Try another email'}
              </Button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t text-center">
            <Link 
              to="/login" 
              className="text-[#84bc00] hover:text-[#6a9600] font-semibold inline-flex items-center"
            >
              <ArrowLeft size={16} className="mr-2" />
              {language === 'es' ? 'Volver al inicio de sesión' : 'Back to Login'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
